/**
 * SCRUM-376 — Cartelería promocional: mismo flujo que etiquetas de góndola
 * (lista de precios → productos → template → hoja → generar) pero con un
 * builder estructurado de estilo/contenido en vez de simples checkboxes de
 * campos — templates prediseñados (OFERTA, DESCUENTO, HASTA X%, SUPER PROMO,
 * precio destacado, precio por kg), NO un editor libre tipo Canva.
 */
import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ClinicalPageShell } from "@/components/clinical";
import { CrudShellHeader, Panel } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import { ProductPickerPanel } from "@/components/shelf-labels/ProductPickerPanel";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useTenantProductFlags } from "@/hooks/use-tenant-product-flags";
import { useProductPicker } from "@/hooks/use-product-picker";
import { toast } from "sonner";
import {
  PROMO_TEMPLATE_LIST, PROMO_TEMPLATES, PROMO_FONT_LABELS, PROMO_SIZE_LABELS,
  defaultPromoStyle, defaultPromoContent,
  type PromoTemplateKey, type PromoFont, type PromoSize,
} from "@/lib/promo-signage/templates";
import { defaultSheetConfig, type SheetConfig, type PaperSize, type Orientation } from "@/lib/shelf-labels/sheet-layout";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";

export const Route = createFileRoute("/app/catalogacion/carteleria")({
  component: PromoSignagePage,
  head: () => ({ meta: [{ title: "Cartelería promocional — Structura Market" }] }),
});

function PromoSignagePage() {
  const tid = useWorkspaceTenantId();
  const navigate = useNavigate();
  const productFlags = useTenantProductFlags();
  const enabled = productFlags?.shelf_labels === true;
  const priceListsEnabled = productFlags?.price_lists === true;

  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const [priceLists, setPriceLists] = useState<Array<{ id: string; name: string }>>([]);
  const [priceListId, setPriceListId] = useState<string | null>(null);

  const picker = useProductPicker(tid);
  const { mode, manualIds, categoriaId, brandId, q, selectedCount } = picker;

  const [templateKey, setTemplateKey] = useState<PromoTemplateKey>("oferta");
  const template = PROMO_TEMPLATES[templateKey];
  const [style, setStyle] = useState(defaultPromoStyle);
  const [content, setContent] = useState(() => defaultPromoContent(template));

  const [sheet, setSheet] = useState<SheetConfig>(() => defaultSheetConfig(template.suggestedWidthMm, template.suggestedHeightMm));

  useEffect(() => {
    setContent(defaultPromoContent(template));
    setSheet(defaultSheetConfig(template.suggestedWidthMm, template.suggestedHeightMm));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateKey]);

  useEffect(() => {
    if (!tid || !enabled) return;
    setLoading(true);
    apiGetJson<{ enabled?: boolean; lists?: Array<{ id: string; name: string }>; _lock?: PlanApiErrorPayload }>("/api/products/price-lists", tid)
      .then((plRes) => {
        if (plRes._lock) {
          setLock(plRes._lock);
          return;
        }
        const lists = plRes.enabled ? (plRes.lists ?? []) : [];
        setPriceLists(lists);
        if (lists.length === 1) setPriceListId(lists[0].id);
      })
      .catch(() => toast.error("No se pudo cargar la configuración"))
      .finally(() => setLoading(false));
  }, [tid, enabled]);

  const canGenerate = selectedCount > 0 && (priceLists.length <= 1 || !!priceListId);

  const generate = async () => {
    if (!tid || !canGenerate) return;
    setGenerating(true);
    try {
      const filter =
        mode === "manual"
          ? { mode: "manual" as const, productIds: [...manualIds] }
          : { mode: "all_filtered" as const, categoriaId: categoriaId || null, brandId: brandId || null, q: q || null };

      const job = await apiSendJson<{ job?: { id: string } }>(
        "/api/shelf-labels/print-jobs",
        "POST",
        {
          kind: "promo_signage",
          priceListId: priceListId || undefined,
          filter,
          productCount: selectedCount,
          templateKey,
          sheetConfig: sheet,
          visualParams: { style, content },
        },
        tid,
      );
      if (!job.job?.id) throw new Error();
      navigate({ to: "/print/carteleria/$jobId", params: { jobId: job.job.id }, search: { tid } });
    } catch {
      toast.error("No se pudo generar el trabajo de impresión");
    } finally {
      setGenerating(false);
    }
  };

  if (!enabled) {
    return (
      <FeatureLockedState
        payload={{
          code: "plan_feature_locked",
          feature: "shelf_labels",
          message: "Etiquetas de góndola y cartelería promocional está disponible en el plan Pro.",
        }}
      />
    );
  }
  if (lock) return <FeatureLockedState payload={lock} />;

  return (
    <div>
      <CrudShellHeader
        title="Cartelería promocional"
        description="Carteles de oferta y promoción para el local"
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/app/catalogacion/historial-impresiones">Ver historial</Link>
          </Button>
        }
      />
      <ClinicalPageShell offset="none" className="space-y-4 pt-6">
        {priceListsEnabled && priceLists.length > 1 ? (
          <Panel title="Lista de precios" description="El precio impreso sale de la lista elegida">
            <Select value={priceListId ?? ""} onValueChange={(v) => setPriceListId(v || null)}>
              <SelectTrigger className="max-w-sm" data-testid="promo-signage-price-list">
                <SelectValue placeholder="Elegí una lista de precios" />
              </SelectTrigger>
              <SelectContent>
                {priceLists.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Panel>
        ) : null}

        <ProductPickerPanel picker={picker} />

        <Panel title="Template" description="Elegí un diseño de cartel">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {PROMO_TEMPLATE_LIST.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTemplateKey(t.key)}
                data-testid={`promo-signage-template-${t.key}`}
                className={`rounded-xl border p-4 text-left transition-colors ${
                  templateKey === t.key ? "border-primary bg-primary/5" : "border-border/60 hover:border-primary/40"
                }`}
              >
                <p className="text-sm font-semibold">{t.name}</p>
                <p className="mt-1 text-xs text-muted-foreground">{t.description}</p>
              </button>
            ))}
          </div>
        </Panel>

        <Panel title="Estilo" description="Colores, tipografía y tamaño — dentro del layout fijo del template">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Color de fondo</label>
              <Input type="color" value={style.bgColor} onChange={(e) => setStyle((s) => ({ ...s, bgColor: e.target.value }))} className="h-9 w-full p-1" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Color de texto</label>
              <Input type="color" value={style.textColor} onChange={(e) => setStyle((s) => ({ ...s, textColor: e.target.value }))} className="h-9 w-full p-1" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Color de acento</label>
              <Input type="color" value={style.accentColor} onChange={(e) => setStyle((s) => ({ ...s, accentColor: e.target.value }))} className="h-9 w-full p-1" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Tipografía</label>
              <Select value={style.font} onValueChange={(v) => setStyle((s) => ({ ...s, font: v as PromoFont }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(PROMO_FONT_LABELS) as PromoFont[]).map((f) => (
                    <SelectItem key={f} value={f}>{PROMO_FONT_LABELS[f]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Tamaño</label>
              <Select value={style.size} onValueChange={(v) => setStyle((s) => ({ ...s, size: v as PromoSize }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(PROMO_SIZE_LABELS) as PromoSize[]).map((sz) => (
                    <SelectItem key={sz} value={sz}>{PROMO_SIZE_LABELS[sz]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Panel>

        <Panel title="Contenido" description="Textos y datos del cartel">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Título / badge</label>
              <Input value={content.headline} onChange={(e) => setContent((c) => ({ ...c, headline: e.target.value }))} placeholder="Ej: OFERTA" />
            </div>
            {template.supportsPercentage ? (
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">% de descuento</label>
                <Input
                  type="number" min={1} max={99}
                  value={content.percentage ?? ""}
                  onChange={(e) => setContent((c) => ({ ...c, percentage: Math.min(99, Math.max(1, Number(e.target.value) || 1)) }))}
                  data-testid="promo-signage-percentage"
                />
              </div>
            ) : null}
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Texto adicional (opcional)</label>
              <Input value={content.subtext ?? ""} onChange={(e) => setContent((c) => ({ ...c, subtext: e.target.value || null }))} placeholder="Ej: Solo fin de semana" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Vigencia (opcional)</label>
              <Input value={content.vigenciaText ?? ""} onChange={(e) => setContent((c) => ({ ...c, vigenciaText: e.target.value || null }))} placeholder="Ej: Válido hasta el 30/09" />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={content.showProductImage} onCheckedChange={(v) => setContent((c) => ({ ...c, showProductImage: v === true }))} />
              Imagen del producto (si tiene)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={content.showLogo} onCheckedChange={(v) => setContent((c) => ({ ...c, showLogo: v === true }))} />
              Logo del negocio
            </label>
          </div>
        </Panel>

        <Panel title="Hoja de impresión" description="Pensado para imprimir en impresora común">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Tamaño</label>
              <Select value={sheet.paperSize} onValueChange={(v) => setSheet((s) => ({ ...s, paperSize: v as PaperSize }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="A4">A4</SelectItem>
                  <SelectItem value="carta">Carta</SelectItem>
                  <SelectItem value="oficio">Oficio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Orientación</label>
              <Select value={sheet.orientation} onValueChange={(v) => setSheet((s) => ({ ...s, orientation: v as Orientation }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vertical">Vertical</SelectItem>
                  <SelectItem value="horizontal">Horizontal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Columnas</label>
              <Input type="number" min={1} max={10} value={sheet.columns} onChange={(e) => setSheet((s) => ({ ...s, columns: Math.max(1, Number(e.target.value) || 1) }))} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Filas</label>
              <Input type="number" min={1} max={20} value={sheet.rows} onChange={(e) => setSheet((s) => ({ ...s, rows: Math.max(1, Number(e.target.value) || 1) }))} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Margen (mm)</label>
              <Input type="number" min={0} max={40} value={sheet.marginTopMm} onChange={(e) => {
                const v = Math.max(0, Number(e.target.value) || 0);
                setSheet((s) => ({ ...s, marginTopMm: v, marginRightMm: v, marginBottomMm: v, marginLeftMm: v }));
              }} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Separación H (mm)</label>
              <Input type="number" min={0} max={20} value={sheet.gapHorizontalMm} onChange={(e) => setSheet((s) => ({ ...s, gapHorizontalMm: Math.max(0, Number(e.target.value) || 0) }))} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Separación V (mm)</label>
              <Input type="number" min={0} max={20} value={sheet.gapVerticalMm} onChange={(e) => setSheet((s) => ({ ...s, gapVerticalMm: Math.max(0, Number(e.target.value) || 0) }))} />
            </div>
          </div>
        </Panel>

        <div className="flex justify-end gap-2">
          <Button
            size="lg"
            disabled={!canGenerate || generating || loading}
            onClick={() => void generate()}
            data-testid="promo-signage-generate"
          >
            {generating ? "Generando…" : `Generar preview (${selectedCount})`}
          </Button>
        </div>
      </ClinicalPageShell>
    </div>
  );
}
